import React from "react";
import classNames from "classnames";
import { format } from "lib/globalize-util";
import { DATATEST } from "constants/integration";
import { themeOptions } from "styles/theme";
import { makeStyles } from "@material-ui/styles";
import type { IScope } from "reducers/scope";
import { normalizePrices } from "../utils/price";

export enum PriceAlignment {
    Left = "left",
    Right = "right",
}

export interface IPricing {
    scope: IScope;
    currency: string;
    product: any;
    alignment?: PriceAlignment;
    isPriceDisplayedInline?: boolean;
    shipStart?: Date;
}

export const Pricing: React.FC<IPricing> = ({
    scope,
    currency,
    product,
    alignment = PriceAlignment.Right,
    isPriceDisplayedInline = false,
    shipStart,
}) => {
    const classes = usePriceStyles();
    const { prices, msrp } = normalizePrices({ scope, currency, product, shipStart });
    return (
        <div
            data-test={DATATEST.GALLERY.ITEM_PREVIEW_PRICE}
            className={classNames(classes.priceSummary, {
                [classes.fullPriceWidth]: isPriceDisplayedInline,
                [classes.priceAlignmentRight]: alignment === PriceAlignment.Right,
                [classes.priceAlignmentLeft]: alignment === PriceAlignment.Left,
            })}
        >
            <div className={classNames({ [classes.oneLinePrice]: isPriceDisplayedInline })}>
                {prices.map(({ price, type, dataTest }, index) => (
                    <div
                        key={type}
                        data-test={dataTest}
                        className={classNames(classes.wholesalePrice, {
                            [classes.small]: price.length > 12,
                            [classes.strikethrough]: index > 0,
                        })}
                    >
                        {price}
                    </div>
                ))}
            </div>
            {msrp && (
                <div className={classes.msrp} data-test={DATATEST.PRODUCT.PRICES.MSRP}>
                    {msrp} {format("MSRP")}
                </div>
            )}
        </div>
    );
};

const usePriceStyles = makeStyles({
    wholesalePrice: {
        color: themeOptions.palette.accent1Color,
        fontSize: "18px",
        letterSpacing: "0.3px",
        lineHeight: 1.2,
        fontWeight: "bold",
    },
    small: {
        color: themeOptions.palette.accent1Color,
        fontSize: "16px",
        letterSpacing: "0.2px",
    },
    strikethrough: {
        marginTop: "5px",
        color: themeOptions.palette.darkerGrey,
        fontSize: "12px",
        fontWeight: "bold",
        letterSpacing: "0.1px",
        lineHeight: 1.67,
        textDecoration: "line-through",
    },
    msrp: {
        color: themeOptions.palette.darkerGrey,
        fontSize: "12px",
        fontWeight: "bold",
        letterSpacing: "0.1px",
        lineHeight: 1.67,
        marginTop: "2px",
    },
    priceSummary: {
        flexShrink: 1,
        flexGrow: 0,
        textAlign: "right",
        width: "160px",
    },
    fullPriceWidth: {
        width: "100%",
    },
    priceAlignmentLeft: {
        textAlign: "left",
    },
    priceAlignmentRight: {
        textAlign: "right",
    },
    oneLinePrice: {
        display: "flex",
        alignItems: "center",
        "& div": {
            marginTop: 0,
            marginRight: "16px",
        },
    },
});
